import {WorkExperience} from "../../../../data/model/work-experience";
import {usePortfolioData} from "../../data";
import {Education} from "../../../../data/model/education";
import {BriefcaseIcon, BuildingIcon, GraduationCapIcon, MapPinIcon, UniversityIcon} from "lucide-react";
import {Badge} from "../../../../components/ui/badge";
import {OptionalLink} from "../../components/optional-link";
import { DateTime } from "luxon";
import {ItemsList} from "../../components/item-list";
import {ListItem} from "../../../../components/list-item";
import {MediaCarousel} from "../../components/media-carousel";
import {SimpleLogo} from "../../components/simple-logo";
import {Tooltip, TooltipContent, TooltipTrigger} from "../../../../components/ui/tooltip";

export function CvSubpage () {
    const { cv } = usePortfolioData();
    return <ItemsList spacing={"compact"} className={"animate-in fade-in duration-300 slide-in-from-bottom-2"}>
        <SummaryBox />
        { cv.map(cvItem => {
            if (cvItem.type === "education") {
                return <EducationItem key={cvItem.institution.name + cvItem.program} education={cvItem} />
            } else if (cvItem.type === "work") {
                return <WorkExperienceItem key={cvItem.company.name + cvItem.role} work={cvItem} />
            }
            return null;
        }) }
    </ItemsList>
}

function EducationItem ({ education }: { education: Education }) {
    return <div className={"border-2 border-accent p-4 !pt-3 rounded-lg"} id={makeItemId(education)}>
        <div className={"flex flex-row items-center text-accent-foreground font-semibold gap-2 opacity-45 text-xs mb-3"}>
            <GraduationCapIcon className={"w-4 h-4"} />
            EDUCATION
        </div>
        <ListItem>
            <ListItem.Logo>
                <SimpleLogo logo={education.institution.logo} className={"w-full"}/>
            </ListItem.Logo>
            <ListItem.Header>
                <div className={"flex flex-col gap-1 text-sm"}>
                    <h3 className={"font-semibold"}>{education.program}</h3>
                    <TimingText item={education}/>
                    <div className={"flex flex-row items-center gap-x-4 gap-y-1 flex-wrap"}>
                        <Tooltip delayDuration={200}>
                            <TooltipTrigger>
                                <OptionalLink to={education.institution.websiteUrl} target={"_blank"}
                                              className={"hover:underline"}>
                                    <div className={"flex flex-row items-center gap-2 font-medium  underline decoration-dotted decoration-foreground/35"}>
                                        <UniversityIcon className={"w-4 h-4"}/>
                                        {education.institution.name}
                                    </div>
                                </OptionalLink>
                            </TooltipTrigger>
                            <TooltipContent className={"max-w-xs whitespace-pre-wrap"}>
                                {education.institution.tooltip}
                            </TooltipContent>
                        </Tooltip>
                        <div className={"flex flex-row items-center gap-2 text-muted-foreground"}>
                            <MapPinIcon className={"w-4 h-4"}/>
                            {education.institution.location}
                        </div>
                    </div>
                </div>
            </ListItem.Header>
            <ListItem.Description>
                <div className={"text text-muted-foreground whitespace-pre-wrap"}>
                    { education.description.trim() }
                </div>
            </ListItem.Description>
        </ListItem>
    </div>
}

function WorkExperienceItem({work}: { work: WorkExperience }) {
    return <div className={"p-4"}>
        <ListItem elementId={makeItemId(work)}>
            <ListItem.Logo>
                <SimpleLogo logo={work.company.logo} className={"w-full"}/>
            </ListItem.Logo>
            <ListItem.Header>
                <div className={"flex flex-col gap-1 text-sm"}>
                    <h3 className={"font-semibold flex flex-row items-center gap-x-4 gap-y-0.5 flex-wrap"}>
                        {work.role}
                        {!work.endDate && <Badge variant={"outline-primary"}>Current Role</Badge>}
                    </h3>
                    <TimingText item={work}/>
                    <div className={"flex flex-row items-center gap-x-4 gap-y-1 flex-wrap"}>
                        <Tooltip delayDuration={200}>
                            <TooltipTrigger>
                                <OptionalLink to={work.company.websiteUrl} target={"_blank"} className={"hover:underline"}>
                                    <div className={"flex flex-row items-center gap-2 font-medium underline decoration-dotted decoration-foreground/35"}>
                                        <BuildingIcon className={"w-4 h-4"}/>
                                        {work.company.name}
                                    </div>
                                </OptionalLink>
                            </TooltipTrigger>
                            <TooltipContent className={"max-w-xs whitespace-pre-wrap"}>
                                {work.company.tooltip}
                            </TooltipContent>
                        </Tooltip>
                        <div className={"flex flex-row items-center gap-2 text-muted-foreground font-medium"}>
                            <MapPinIcon className={"w-4 h-4"}/>
                            {work.location}
                        </div>
                    </div>
                </div>
            </ListItem.Header>
            <ListItem.Description>
                <div className={"flex flex-col gap-3"}>
                    <ul className={"text-muted-foreground list-disc list-outside ps-5"}>
                        { work.description.map(di => <li className={"mb-1"}>{ di.trim() }</li>)}
                    </ul>
                    { work.media && <MediaCarousel media={work.media} /> }
                </div>
            </ListItem.Description>
        </ListItem>
    </div>
}

function SummaryBox () {
    const portfolio = usePortfolioData();

    const work = portfolio.cv.filter(item => item.type === "work") as WorkExperience[];
    const education = portfolio.cv.filter(item => item.type === "education") as Education[];

    return <div className={"border rounded-lg px-4 py-3 flex flex-col items-stretch gap-4"}>
        <div className={"flex flex-row items-center gap-x-6 flex-wrap"}>
            <div className={"text-sm font-semibold text-primary"}>AT A GLANCE</div>
            <div className={"text-muted-foreground text-xs"}>Click any item to learn more</div>
        </div>
        <div className={"flex flex-row gap-6 text-sm"}>
            <div className={"basis-1/2 flex flex-col items-start gap-3"}>
                { work.map((w, i) => (
                    <SummaryBoxItem
                        linkId={makeItemId(w)}
                        key={i}
                        header={w.role}
                        date={w.startDate}
                        subtitlePrimary={w.company.name}
                        subtitleSecondary={duration(w)}
                    />
                ))}
            </div>
            <div className={"basis-1/2 flex flex-col items-start gap-3"}>
                {education.map((edu, i) => (
                    <SummaryBoxItem
                        linkId={makeItemId(edu)}
                        key={i}
                        header={edu.program}
                        date={edu.endDate}
                        subtitleSecondary={edu.institution.name}
                        icon={"edu"}
                    />
                )) }
            </div>
        </div>
    </div>
}

function SummaryBoxItem ({ header, date, subtitlePrimary, subtitleSecondary, icon, linkId }: { header: string, date: string, subtitlePrimary?: string, subtitleSecondary?: string, icon?: "work" | "edu", linkId: string }) {
    const scrollToItem = () => {
        const itemTop = document.getElementById(linkId)!.offsetTop;
        window.scrollTo({
            top: itemTop - 120,
            behavior: "smooth",
        });
    }
    return <div className={"flex flex-row items-start gap-x-4 flex-wrap sm:flex-nowrap group"} onClick={scrollToItem} style={{ cursor: "pointer" }}>
        <div className={"flex flex-row items-center gap-3 text-muted-foreground w-32 sm:w-auto group-hover:translate-x-0.5 transition-all duration-300"}>
            <div className={"font-medium w-8"}>
                {DateTime.fromISO(date).toFormat("yyyy")}
            </div>
            {icon === "edu" && <GraduationCapIcon className={"w-4 h-4"}/>}
            {icon === "work" && <BriefcaseIcon className={"w-4 h-4"}/>}
        </div>
        <div className={"flex flex-col items-start group-hover:translate-x-[-1px] transition-all duration-300"}>
            <div className={"font-medium underline decoration-background group-hover:decoration-foreground transition-all duration-300"}>{header}</div>
            <div className={"text-muted-foreground text-xs"}>
                { subtitlePrimary && <b>{subtitlePrimary}</b> }
                { subtitlePrimary && subtitleSecondary && " - " }
                { subtitleSecondary && subtitleSecondary }
            </div>
        </div>
    </div>
}

function TimingText({item}: { item: { startDate: string, endDate?: string } }) {
    return <div className={"text-xs font-medium mb-1"}>
        {!item.endDate ? "since " : ""}
        {monthYearDateFormat(item.startDate)}
        {item.endDate ? ` - ${monthYearDateFormat(item.endDate)}` : ""}
        {` (${duration(item)})`}
    </div>
}

function monthYearDateFormat(date: string) {
    return DateTime.fromISO(date).toFormat("MMM yyyy");
}

function duration(item: { startDate: string, endDate?: string }) {
    const end = item.endDate ? DateTime.fromISO(item.endDate) : DateTime.now();
    const start = DateTime.fromISO(item.startDate);
    const diff = end.diff(start, ["years", "months"]).toObject();
    const formatted = (n: number | undefined, unit: string) => {
        if (!n) return "";
        const ceiled = Math.ceil(n);
        return `${ceiled} ${unit}${ceiled !== 1 ? "s" : ""}`;
    };
    return `${formatted(diff.years, "year")} ${formatted(diff.months, "month")}`.trim();
}

function makeItemId (item: WorkExperience | Education) {
    if (item.type === "work") {
        return `work-${item.company.name.replace(/\s+/g, "-").toLowerCase()}-${item.role.replace(/\s+/g, "-").toLowerCase()}`;
    } else if (item.type === "education") {
        return `edu-${item.institution.name.replace(/\s+/g, "-").toLowerCase()}-${item.program.replace(/\s+/g, "-").toLowerCase()}`;
    }
    throw new Error("Unknown item type");
}